<template>
    <div class="page">
        <div class="page-header">
            <div>
                <h1 class="page-title">{{ t('package_charges') }}</h1>
                <p class="page-subtitle">{{ t('package_charges_subtitle') }}</p>
            </div>
        </div>

        <div class="tabs">
            <button type="button" class="tab" :class="{ active: activeTab === 'plans' }" @click="setTab('plans')">{{ t('plans') }}</button>
            <button type="button" class="tab" :class="{ active: activeTab === 'owner_rent' }" @click="setTab('owner_rent')">{{ t('owner_rent_packages') }}</button>
            <button type="button" class="tab" :class="{ active: activeTab === 'owner_space' }" @click="setTab('owner_space')">{{ t('owner_space_packages') }}</button>
        </div>

        <div class="card">
            <div class="datatable-toolbar">
                <div class="toolbar-left">
                    <select v-model.number="perPage" class="length-select" :disabled="loading" @change="resetPageAndReload">
                        <option :value="10">10</option>
                        <option :value="20">20</option>
                        <option :value="50">50</option>
                        <option :value="100">100</option>
                    </select>

                    <select v-model="statusFilter" class="status-select" :disabled="loading" @change="resetPageAndReload">
                        <option value="all">{{ t('all') }}</option>
                        <option value="active">{{ t('active') }}</option>
                        <option value="inactive">{{ t('inactive') }}</option>
                    </select>

                    <select v-if="activeTab !== 'plans'" v-model="propertyTypeFilter" class="status-select" :disabled="loading" @change="resetPageAndReload">
                        <option value="">{{ t('all_property_types') }}</option>
                        <option v-for="pt in propertyTypes" :key="pt.PropertyTypeID" :value="String(pt.PropertyTypeID)">{{ propertyTypeLabel(pt) }}</option>
                    </select>

                    <button type="button" class="tool-btn tool-btn-icon" @click="toggleFilters" :disabled="loading">
                        <svg width="18" height="18" viewBox="0 0 20 20" fill="none">
                            <path d="M2.5 4.16667H17.5M5.83333 10H14.1667M8.33333 15.8333H11.6667" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                        </svg>
                    </button>

                    <button type="button" class="tool-btn tool-btn-primary" @click="refreshActiveTab" :disabled="loading">{{ t('refresh') }}</button>
                    <button type="button" class="tool-btn tool-btn-light" @click="clearFilters" :disabled="!hasAnyFilter">{{ t('clear_filters') }}</button>
                    <button type="button" class="tool-btn tool-btn-success" @click="openCreate" :disabled="loading">{{ t('create') }}</button>
                </div>

                <div class="toolbar-right">
                    <div class="search-box">
                        <svg width="20" height="20" viewBox="0 0 20 20" fill="none">
                            <path d="M9.16667 15.8333C12.8486 15.8333 15.8333 12.8486 15.8333 9.16667C15.8333 5.48477 12.8486 2.5 9.16667 2.5C5.48477 2.5 2.5 5.48477 2.5 9.16667C2.5 12.8486 5.48477 15.8333 9.16667 15.8333Z" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                            <path d="M17.5 17.5L13.875 13.875" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
                        </svg>
                        <input v-model="searchQuery" type="text" class="search-input" :placeholder="t('search') + '...'" @input="debouncedSearch" :disabled="loading">
                    </div>
                </div>
            </div>

            <div class="table-container">
                <table v-if="activeTab === 'plans'" class="data-table">
                    <thead>
                        <tr>
                            <th>
                                <input v-if="showFilters" v-model="planFilters.id" type="text" class="th-input" :placeholder="t('id')">
                                <span v-else>{{ t('id') }}</span>
                            </th>
                            <th>
                                <input v-if="showFilters" v-model="planFilters.name" type="text" class="th-input" :placeholder="t('plan_name')">
                                <span v-else>{{ t('plan_name') }}</span>
                            </th>
                            <th class="mono">
                                <input v-if="showFilters" v-model="planFilters.rate" type="text" class="th-input" :placeholder="t('rate')">
                                <span v-else>{{ t('rate') }}</span>
                            </th>
                            <th class="mono">
                                <input v-if="showFilters" v-model="planFilters.validity" type="text" class="th-input" :placeholder="t('validity_days')">
                                <span v-else>{{ t('validity_days') }}</span>
                            </th>
                            <th class="mono">
                                <input v-if="showFilters" v-model="planFilters.order" type="text" class="th-input" :placeholder="t('order')">
                                <span v-else>{{ t('order') }}</span>
                            </th>
                            <th>
                                <select v-if="showFilters" v-model="planFilters.status" class="th-select">
                                    <option value="">{{ t('status') }}</option>
                                    <option value="active">{{ t('active') }}</option>
                                    <option value="inactive">{{ t('inactive') }}</option>
                                </select>
                                <span v-else>{{ t('status') }}</span>
                            </th>
                            <th>{{ t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td colspan="7" style="padding: 10px;">
                                <div class="skeleton-wrap">
                                    <div v-for="n in 6" :key="n" class="skeleton-row"></div>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="row in filteredPlans" v-if="!loading" :key="row.PlanID">
                            <td class="mono">{{ row.PlanID }}</td>
                            <td>
                                <div class="cell-main">{{ planName(row) }}</div>
                                <div class="cell-sub">{{ planNameAlt(row) }}</div>
                            </td>
                            <td class="mono">{{ row.Rate ?? '-' }}</td>
                            <td class="mono">{{ row.VilidityDays ?? '-' }}</td>
                            <td class="mono">{{ row.Order_SL ?? '-' }}</td>
                            <td>
                                <span :class="['badge', row.Status ? 'badge-success' : 'badge-muted']">{{ row.Status ? t('active') : t('inactive') }}</span>
                            </td>
                            <td class="actions">
                                <button type="button" class="btn btn-small btn-secondary" @click="openEdit(row)" :disabled="busyIds[`plan_${row.PlanID}`]">{{ t('edit') }}</button>
                                <button type="button" class="btn btn-small" @click="togglePlan(row)" :disabled="busyIds[`plan_${row.PlanID}`]">
                                    {{ row.Status ? t('deactivate') : t('activate') }}
                                </button>
                                <button type="button" class="btn btn-small btn-danger" @click="removePlan(row.PlanID)" :disabled="busyIds[`plan_${row.PlanID}`]">{{ t('delete') }}</button>
                            </td>
                        </tr>
                        <tr v-if="!loading && !filteredPlans.length">
                            <td colspan="7" class="empty">{{ t('no_data') }}</td>
                        </tr>
                    </tbody>
                </table>

                <table v-else-if="activeTab === 'owner_rent'" class="data-table">
                    <thead>
                        <tr>
                            <th>
                                <input v-if="showFilters" v-model="pkgFilters.id" type="text" class="th-input" :placeholder="t('id')">
                                <span v-else>{{ t('id') }}</span>
                            </th>
                            <th>
                                <input v-if="showFilters" v-model="pkgFilters.name" type="text" class="th-input" :placeholder="t('package_name')">
                                <span v-else>{{ t('package_name') }}</span>
                            </th>
                            <th>
                                <input v-if="showFilters" v-model="pkgFilters.property_type" type="text" class="th-input" :placeholder="t('property_type')">
                                <span v-else>{{ t('property_type') }}</span>
                            </th>
                            <th>
                                <input v-if="showFilters" v-model="pkgFilters.unit" type="text" class="th-input" :placeholder="t('unit')">
                                <span v-else>{{ t('unit') }}</span>
                            </th>
                            <th class="mono">
                                <input v-if="showFilters" v-model="pkgFilters.range" type="text" class="th-input" :placeholder="t('range')">
                                <span v-else>{{ t('range') }}</span>
                            </th>
                            <th class="mono">
                                <input v-if="showFilters" v-model="pkgFilters.rate" type="text" class="th-input" :placeholder="t('rate')">
                                <span v-else>{{ t('rate') }}</span>
                            </th>
                            <th>
                                <select v-if="showFilters" v-model="pkgFilters.status" class="th-select">
                                    <option value="">{{ t('status') }}</option>
                                    <option value="active">{{ t('active') }}</option>
                                    <option value="inactive">{{ t('inactive') }}</option>
                                </select>
                                <span v-else>{{ t('status') }}</span>
                            </th>
                            <th>{{ t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td colspan="8" style="padding: 10px;">
                                <div class="skeleton-wrap">
                                    <div v-for="n in 6" :key="n" class="skeleton-row"></div>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="row in filteredOwnerRent" v-if="!loading" :key="row.OwnerPackageRentID">
                            <td class="mono">{{ row.OwnerPackageRentID }}</td>
                            <td>{{ row.PackageName || '-' }}</td>
                            <td>{{ row.PropertyTypeName || '-' }}</td>
                            <td>{{ row.Unit || '-' }}</td>
                            <td class="mono">{{ row.RangeFrom }} - {{ row.RangeTo }}</td>
                            <td class="mono">{{ row.PackageRate }}</td>
                            <td>
                                <span :class="['badge', row.Status ? 'badge-success' : 'badge-muted']">{{ row.Status ? t('active') : t('inactive') }}</span>
                            </td>
                            <td class="actions">
                                <button type="button" class="btn btn-small btn-secondary" @click="openEdit(row)" :disabled="busyIds[`rent_${row.OwnerPackageRentID}`]">{{ t('edit') }}</button>
                                <button type="button" class="btn btn-small" @click="toggleOwnerRent(row)" :disabled="busyIds[`rent_${row.OwnerPackageRentID}`]">
                                    {{ row.Status ? t('deactivate') : t('activate') }}
                                </button>
                                <button type="button" class="btn btn-small btn-danger" @click="removeOwnerRent(row.OwnerPackageRentID)" :disabled="busyIds[`rent_${row.OwnerPackageRentID}`]">{{ t('delete') }}</button>
                            </td>
                        </tr>
                        <tr v-if="!loading && !filteredOwnerRent.length">
                            <td colspan="8" class="empty">{{ t('no_data') }}</td>
                        </tr>
                    </tbody>
                </table>

                <table v-else class="data-table">
                    <thead>
                        <tr>
                            <th>
                                <input v-if="showFilters" v-model="pkgFilters.id" type="text" class="th-input" :placeholder="t('id')">
                                <span v-else>{{ t('id') }}</span>
                            </th>
                            <th>
                                <input v-if="showFilters" v-model="pkgFilters.name" type="text" class="th-input" :placeholder="t('package_name')">
                                <span v-else>{{ t('package_name') }}</span>
                            </th>
                            <th>
                                <input v-if="showFilters" v-model="pkgFilters.property_type" type="text" class="th-input" :placeholder="t('property_type')">
                                <span v-else>{{ t('property_type') }}</span>
                            </th>
                            <th>
                                <input v-if="showFilters" v-model="pkgFilters.unit" type="text" class="th-input" :placeholder="t('unit')">
                                <span v-else>{{ t('unit') }}</span>
                            </th>
                            <th class="mono">
                                <input v-if="showFilters" v-model="pkgFilters.range" type="text" class="th-input" :placeholder="t('range')">
                                <span v-else>{{ t('range') }}</span>
                            </th>
                            <th class="mono">
                                <input v-if="showFilters" v-model="pkgFilters.rate" type="text" class="th-input" :placeholder="t('rate')">
                                <span v-else>{{ t('rate') }}</span>
                            </th>
                            <th>
                                <select v-if="showFilters" v-model="pkgFilters.status" class="th-select">
                                    <option value="">{{ t('status') }}</option>
                                    <option value="active">{{ t('active') }}</option>
                                    <option value="inactive">{{ t('inactive') }}</option>
                                </select>
                                <span v-else>{{ t('status') }}</span>
                            </th>
                            <th>{{ t('actions') }}</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr v-if="loading">
                            <td colspan="8" style="padding: 10px;">
                                <div class="skeleton-wrap">
                                    <div v-for="n in 6" :key="n" class="skeleton-row"></div>
                                </div>
                            </td>
                        </tr>
                        <tr v-for="row in filteredOwnerSpace" v-if="!loading" :key="row.OwnerPackageSpaceID">
                            <td class="mono">{{ row.OwnerPackageSpaceID }}</td>
                            <td>{{ row.PackageName || '-' }}</td>
                            <td>{{ row.PropertyTypeName || '-' }}</td>
                            <td>{{ row.Unit || '-' }}</td>
                            <td class="mono">{{ row.RangeFrom }} - {{ row.RangeTo }}</td>
                            <td class="mono">{{ row.PackageRate }}</td>
                            <td>
                                <span :class="['badge', row.Status ? 'badge-success' : 'badge-muted']">{{ row.Status ? t('active') : t('inactive') }}</span>
                            </td>
                            <td class="actions">
                                <button type="button" class="btn btn-small btn-secondary" @click="openEdit(row)" :disabled="busyIds[`space_${row.OwnerPackageSpaceID}`]">{{ t('edit') }}</button>
                                <button type="button" class="btn btn-small" @click="toggleOwnerSpace(row)" :disabled="busyIds[`space_${row.OwnerPackageSpaceID}`]">
                                    {{ row.Status ? t('deactivate') : t('activate') }}
                                </button>
                                <button type="button" class="btn btn-small btn-danger" @click="removeOwnerSpace(row.OwnerPackageSpaceID)" :disabled="busyIds[`space_${row.OwnerPackageSpaceID}`]">{{ t('delete') }}</button>
                            </td>
                        </tr>
                        <tr v-if="!loading && !filteredOwnerSpace.length">
                            <td colspan="8" class="empty">{{ t('no_data') }}</td>
                        </tr>
                    </tbody>
                </table>

                <div class="pager">
                    <div class="pager-left">{{ showingText }}</div>
                    <div class="pager-right">
                        <button class="pager-btn" @click="goFirst" :disabled="loading || page <= 1">{{ t('first') }}</button>
                        <button class="pager-btn" @click="prevPage" :disabled="loading || page <= 1">{{ t('previous') }}</button>
                        <button v-for="p in pageButtons" :key="p" class="pager-num" :class="{ active: p === page }" @click="goPage(p)" :disabled="loading">
                            {{ p }}
                        </button>
                        <button class="pager-btn" @click="nextPage" :disabled="loading || page >= totalPages">{{ t('next') }}</button>
                        <button class="pager-btn" @click="goLast" :disabled="loading || page >= totalPages">{{ t('last') }}</button>
                    </div>
                </div>
            </div>
        </div>

        <Teleport to="body">
            <div v-if="showFormModal" class="modal-overlay" @click.self="closeForm">
                <div class="modal modal-large">
                    <div class="modal-header">
                        <h3>{{ modalTitle }}</h3>
                        <button class="modal-close" @click="closeForm">×</button>
                    </div>
                    <div class="modal-body">
                        <div v-if="activeTab === 'plans'" class="form-grid">
                            <div class="form-group span-2">
                                <label class="form-label">{{ t('plan_name') }}</label>
                                <input v-model="planForm.plan_name" type="text" class="form-input">
                            </div>
                            <div class="form-group span-2">
                                <label class="form-label">{{ t('plan_name_bn') }}</label>
                                <input v-model="planForm.plan_name_bn" type="text" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('validity_days') }}</label>
                                <input v-model="planForm.vilidity_days" type="number" min="0" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('rate') }}</label>
                                <input v-model="planForm.rate" type="number" min="0" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('rate_foreigner') }}</label>
                                <input v-model="planForm.rate_foreigner" type="number" min="0" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('order') }}</label>
                                <input v-model="planForm.order_sl" type="number" min="0" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('max_browsing_qty') }}</label>
                                <input v-model="planForm.max_browsing_qty" type="number" min="0" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('max_contact_view') }}</label>
                                <input v-model="planForm.max_contact_view" type="number" min="0" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('can_view_contact_details') }}</label>
                                <select v-model="planForm.can_view_contact_details" class="form-input">
                                    <option :value="true">{{ t('yes') }}</option>
                                    <option :value="false">{{ t('no') }}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('save_favourite_listings') }}</label>
                                <select v-model="planForm.save_favourite_listings" class="form-input">
                                    <option :value="true">{{ t('yes') }}</option>
                                    <option :value="false">{{ t('no') }}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('allow_preference_setup') }}</label>
                                <select v-model="planForm.allow_preference_setup" class="form-input">
                                    <option :value="true">{{ t('yes') }}</option>
                                    <option :value="false">{{ t('no') }}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('get_auto_notification') }}</label>
                                <select v-model="planForm.get_auto_notification" class="form-input">
                                    <option :value="true">{{ t('yes') }}</option>
                                    <option :value="false">{{ t('no') }}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('view_location_on_map') }}</label>
                                <select v-model="planForm.view_location_on_map" class="form-input">
                                    <option :value="true">{{ t('yes') }}</option>
                                    <option :value="false">{{ t('no') }}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('status') }}</label>
                                <select v-model="planForm.status" class="form-input">
                                    <option :value="true">{{ t('active') }}</option>
                                    <option :value="false">{{ t('inactive') }}</option>
                                </select>
                            </div>
                        </div>

                        <div v-else class="form-grid">
                            <div class="form-group">
                                <label class="form-label">{{ t('property_type') }}</label>
                                <select v-model="pkgForm.property_type_id" class="form-input">
                                    <option value="">{{ t('select') }}</option>
                                    <option v-for="pt in propertyTypes" :key="pt.PropertyTypeID" :value="String(pt.PropertyTypeID)">{{ propertyTypeLabel(pt) }}</option>
                                </select>
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('package_name') }}</label>
                                <input v-model="pkgForm.package_name" type="text" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('unit') }}</label>
                                <input v-model="pkgForm.unit" type="text" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('range_from') }}</label>
                                <input v-model="pkgForm.range_from" type="number" step="0.01" min="0" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('range_to') }}</label>
                                <input v-model="pkgForm.range_to" type="number" step="0.01" min="0" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('rate') }}</label>
                                <input v-model="pkgForm.package_rate" type="number" min="0" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('validity_from') }}</label>
                                <input v-model="pkgForm.validity_from" type="date" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('validity_to') }}</label>
                                <input v-model="pkgForm.validity_to" type="date" class="form-input">
                            </div>
                            <div class="form-group">
                                <label class="form-label">{{ t('status') }}</label>
                                <select v-model="pkgForm.status" class="form-input">
                                    <option :value="true">{{ t('active') }}</option>
                                    <option :value="false">{{ t('inactive') }}</option>
                                </select>
                            </div>
                        </div>
                    </div>
                    <div class="modal-actions">
                        <button type="button" class="btn btn-secondary" @click="closeForm">{{ t('cancel') }}</button>
                        <button type="button" class="btn btn-primary" @click="save" :disabled="saving">{{ t('save') }}</button>
                    </div>
                </div>
            </div>

            <div class="toast-container">
                <TransitionGroup name="toast" tag="div">
                    <div v-for="toast in toasts" :key="toast.id" :class="['toast', `toast-${toast.type}`]">
                        <div class="toast-content">
                            <p class="toast-message">{{ toast.message }}</p>
                        </div>
                        <button @click="removeToast(toast.id)" class="toast-close">
                            <svg width="16" height="16" viewBox="0 0 20 20" fill="none">
                                <path d="M15 5L5 15M5 5L15 15" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                            </svg>
                        </button>
                    </div>
                </TransitionGroup>
            </div>
        </Teleport>
    </div>
</template>

<script>
import { computed, inject, onMounted, ref } from 'vue';

export default {
    name: 'PackageChargesPage',
    setup() {
        const adminT = inject('adminT', (k) => k);
        const adminLang = inject('adminLang', ref('en'));

        const t = (key) => adminT(key);
        const langValue = () => (adminLang?.value === 'bn' ? 'bn' : 'en');

        const activeTab = ref('plans');
        const statusFilter = ref('all');
        const propertyTypeFilter = ref('');
        const searchQuery = ref('');
        const page = ref(1);
        const perPage = ref(20);
        const pagination = ref({ page: 1, per_page: 20, total: 0 });
        let searchTimer = null;

        const loading = ref(true);
        const saving = ref(false);
        const busyIds = ref({});

        const propertyTypes = ref([]);

        const plans = ref([]);
        const ownerRent = ref([]);
        const ownerSpace = ref([]);

        const showFilters = ref(true);
        const planFilters = ref({
            id: '',
            name: '',
            rate: '',
            validity: '',
            order: '',
            status: '',
        });
        const pkgFilters = ref({
            id: '',
            name: '',
            property_type: '',
            unit: '',
            range: '',
            rate: '',
            status: '',
        });

        const resetPage = () => {
            page.value = 1;
        };

        const showFormModal = ref(false);
        const formMode = ref('create');
        const editingId = ref(null);

        const planForm = ref({
            plan_name: '',
            plan_name_bn: '',
            vilidity_days: '',
            rate: '',
            rate_foreigner: '',
            max_browsing_qty: '',
            max_contact_view: '',
            can_view_contact_details: false,
            save_favourite_listings: false,
            allow_preference_setup: false,
            get_auto_notification: false,
            view_location_on_map: false,
            order_sl: '',
            status: true,
        });

        const pkgForm = ref({
            property_type_id: '',
            package_name: '',
            unit: '',
            range_from: '',
            range_to: '',
            package_rate: '',
            validity_from: '',
            validity_to: '',
            status: true,
        });

        const toasts = ref([]);
        const addToast = (type, message) => {
            const id = Date.now() + Math.random();
            toasts.value.push({ id, type, message });
            setTimeout(() => removeToast(id), 4500);
        };
        const removeToast = (id) => {
            toasts.value = toasts.value.filter((x) => x.id !== id);
        };

        const propertyTypeLabel = (pt) => {
            const isBn = adminLang?.value === 'bn';
            return isBn ? (pt.PropertyTypeName_BN || pt.PropertyTypeName) : (pt.PropertyTypeName || pt.PropertyTypeName_BN);
        };

        const planName = (row) => {
            const isBn = adminLang?.value === 'bn';
            return isBn ? (row.PlanName_BN || row.PlanName) : (row.PlanName || row.PlanName_BN);
        };

        const planNameAlt = (row) => {
            const isBn = adminLang?.value === 'bn';
            return isBn ? (row.PlanName || '') : (row.PlanName_BN || '');
        };

        const modalTitle = computed(() => {
            if (activeTab.value === 'plans') return formMode.value === 'create' ? t('create_plan') : t('edit_plan');
            if (activeTab.value === 'owner_rent') return formMode.value === 'create' ? t('create_owner_rent') : t('edit_owner_rent');
            return formMode.value === 'create' ? t('create_owner_space') : t('edit_owner_space');
        });

        const loadPropertyTypes = async () => {
            try {
                const res = await window.axios.get('/api/v1/property-types', { params: { lang: langValue() } });
                propertyTypes.value = res.data?.data?.items || res.data?.data || [];
            } catch {
                propertyTypes.value = [];
            }
        };

        const loadPlans = async () => {
            loading.value = true;
            try {
                const res = await window.axios.get('/admin/api/package-charges/plans', {
                    params: {
                        status: statusFilter.value,
                        page: page.value,
                        per_page: perPage.value,
                        q: searchQuery.value || '',
                        lang: langValue(),
                    },
                });
                const items = res.data?.data?.items || res.data?.data || [];
                plans.value = items;
                const nextPagination = res.data?.data?.pagination || { page: 1, per_page: perPage.value, total: Array.isArray(items) ? items.length : 0 };
                pagination.value = nextPagination;
                page.value = Number(nextPagination.page || 1);
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                loading.value = false;
            }
        };

        const loadOwnerRent = async () => {
            loading.value = true;
            try {
                const res = await window.axios.get('/admin/api/package-charges/owner-rent', {
                    params: {
                        status: statusFilter.value,
                        property_type_id: propertyTypeFilter.value || undefined,
                        page: page.value,
                        per_page: perPage.value,
                        q: searchQuery.value || '',
                        lang: langValue(),
                    },
                });
                const items = res.data?.data?.items || res.data?.data || [];
                ownerRent.value = items;
                const nextPagination = res.data?.data?.pagination || { page: 1, per_page: perPage.value, total: Array.isArray(items) ? items.length : 0 };
                pagination.value = nextPagination;
                page.value = Number(nextPagination.page || 1);
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                loading.value = false;
            }
        };

        const loadOwnerSpace = async () => {
            loading.value = true;
            try {
                const res = await window.axios.get('/admin/api/package-charges/owner-space', {
                    params: {
                        status: statusFilter.value,
                        property_type_id: propertyTypeFilter.value || undefined,
                        page: page.value,
                        per_page: perPage.value,
                        q: searchQuery.value || '',
                        lang: langValue(),
                    },
                });
                const items = res.data?.data?.items || res.data?.data || [];
                ownerSpace.value = items;
                const nextPagination = res.data?.data?.pagination || { page: 1, per_page: perPage.value, total: Array.isArray(items) ? items.length : 0 };
                pagination.value = nextPagination;
                page.value = Number(nextPagination.page || 1);
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed_to_load'));
            } finally {
                loading.value = false;
            }
        };

        const refreshActiveTab = async () => {
            if (activeTab.value === 'plans') return loadPlans();
            if (activeTab.value === 'owner_rent') return loadOwnerRent();
            return loadOwnerSpace();
        };

        const resetPageAndReload = async () => {
            page.value = 1;
            await refreshActiveTab();
        };

        const setTab = async (tab) => {
            activeTab.value = tab;
            page.value = 1;
            searchQuery.value = '';
            await refreshActiveTab();
        };

        const totalPages = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || pagination.value?.per_page || 20);
            return Math.max(1, Math.ceil(total / Math.max(1, size)));
        });

        const normalize = (v) => (v ?? '').toString().toLowerCase();

        const filteredPlans = computed(() => {
            const list = Array.isArray(plans.value) ? plans.value : [];
            const f = planFilters.value || {};
            const idQ = normalize(f.id).trim();
            const nameQ = normalize(f.name).trim();
            const rateQ = normalize(f.rate).trim();
            const validityQ = normalize(f.validity).trim();
            const orderQ = normalize(f.order).trim();
            const statusQ = (f.status || '').toString().trim().toLowerCase();

            if (!idQ && !nameQ && !rateQ && !validityQ && !orderQ && !statusQ) return list;

            return list.filter((row) => {
                const id = normalize(row.PlanID);
                const name = normalize(planName(row));
                const rate = normalize(row.Rate);
                const validity = normalize(row.VilidityDays);
                const order = normalize(row.Order_SL);
                const isActive = !!row.Status;

                if (idQ && !id.includes(idQ)) return false;
                if (nameQ && !name.includes(nameQ)) return false;
                if (rateQ && !rate.includes(rateQ)) return false;
                if (validityQ && !validity.includes(validityQ)) return false;
                if (orderQ && !order.includes(orderQ)) return false;
                if (statusQ === 'active' && !isActive) return false;
                if (statusQ === 'inactive' && isActive) return false;
                return true;
            });
        });

        const filteredOwnerRent = computed(() => {
            const list = Array.isArray(ownerRent.value) ? ownerRent.value : [];
            const f = pkgFilters.value || {};
            const idQ = normalize(f.id).trim();
            const nameQ = normalize(f.name).trim();
            const typeQ = normalize(f.property_type).trim();
            const unitQ = normalize(f.unit).trim();
            const rangeQ = normalize(f.range).trim();
            const rateQ = normalize(f.rate).trim();
            const statusQ = (f.status || '').toString().trim().toLowerCase();

            if (!idQ && !nameQ && !typeQ && !unitQ && !rangeQ && !rateQ && !statusQ) return list;

            return list.filter((row) => {
                const id = normalize(row.OwnerPackageRentID);
                const name = normalize(row.PackageName);
                const type = normalize(row.PropertyTypeName);
                const unit = normalize(row.Unit);
                const range = `${normalize(row.RangeFrom)} - ${normalize(row.RangeTo)}`;
                const rate = normalize(row.PackageRate);
                const isActive = !!row.Status;

                if (idQ && !id.includes(idQ)) return false;
                if (nameQ && !name.includes(nameQ)) return false;
                if (typeQ && !type.includes(typeQ)) return false;
                if (unitQ && !unit.includes(unitQ)) return false;
                if (rangeQ && !range.includes(rangeQ)) return false;
                if (rateQ && !rate.includes(rateQ)) return false;
                if (statusQ === 'active' && !isActive) return false;
                if (statusQ === 'inactive' && isActive) return false;
                return true;
            });
        });

        const filteredOwnerSpace = computed(() => {
            const list = Array.isArray(ownerSpace.value) ? ownerSpace.value : [];
            const f = pkgFilters.value || {};
            const idQ = normalize(f.id).trim();
            const nameQ = normalize(f.name).trim();
            const typeQ = normalize(f.property_type).trim();
            const unitQ = normalize(f.unit).trim();
            const rangeQ = normalize(f.range).trim();
            const rateQ = normalize(f.rate).trim();
            const statusQ = (f.status || '').toString().trim().toLowerCase();

            if (!idQ && !nameQ && !typeQ && !unitQ && !rangeQ && !rateQ && !statusQ) return list;

            return list.filter((row) => {
                const id = normalize(row.OwnerPackageSpaceID);
                const name = normalize(row.PackageName);
                const type = normalize(row.PropertyTypeName);
                const unit = normalize(row.Unit);
                const range = `${normalize(row.RangeFrom)} - ${normalize(row.RangeTo)}`;
                const rate = normalize(row.PackageRate);
                const isActive = !!row.Status;

                if (idQ && !id.includes(idQ)) return false;
                if (nameQ && !name.includes(nameQ)) return false;
                if (typeQ && !type.includes(typeQ)) return false;
                if (unitQ && !unit.includes(unitQ)) return false;
                if (rangeQ && !range.includes(rangeQ)) return false;
                if (rateQ && !rate.includes(rateQ)) return false;
                if (statusQ === 'active' && !isActive) return false;
                if (statusQ === 'inactive' && isActive) return false;
                return true;
            });
        });

        const filteredCount = computed(() => {
            if (activeTab.value === 'plans') return filteredPlans.value.length;
            if (activeTab.value === 'owner_rent') return filteredOwnerRent.value.length;
            return filteredOwnerSpace.value.length;
        });

        const hasAnyFilter = computed(() => {
            if ((searchQuery.value || '').trim() !== '') return true;
            if ((statusFilter.value || 'all') !== 'all') return true;
            if (activeTab.value !== 'plans' && (propertyTypeFilter.value || '').trim() !== '') return true;

            if (activeTab.value === 'plans') {
                const f = planFilters.value || {};
                return Object.values(f).some((x) => String(x || '').trim() !== '');
            }
            const f = pkgFilters.value || {};
            return Object.values(f).some((x) => String(x || '').trim() !== '');
        });

        const toggleFilters = () => {
            showFilters.value = !showFilters.value;
        };

        const clearFilters = async () => {
            searchQuery.value = '';
            statusFilter.value = 'all';
            propertyTypeFilter.value = '';
            planFilters.value = { id: '', name: '', rate: '', validity: '', order: '', status: '' };
            pkgFilters.value = { id: '', name: '', property_type: '', unit: '', range: '', rate: '', status: '' };
            page.value = 1;
            await refreshActiveTab();
        };

        const pageButtons = computed(() => {
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const total = totalPages.value;
            const max = 5;
            let start = Math.max(1, current - Math.floor(max / 2));
            let end = start + max - 1;
            if (end > total) {
                end = total;
                start = Math.max(1, end - max + 1);
            }
            const out = [];
            for (let p = start; p <= end; p++) out.push(p);
            return out;
        });

        const showingText = computed(() => {
            const total = Number(pagination.value?.total || 0);
            const size = Number(perPage.value || pagination.value?.per_page || 20);
            const current = Math.min(Math.max(1, page.value || 1), totalPages.value);
            const from = total === 0 ? 0 : (current - 1) * size + 1;
            const to = total === 0 ? 0 : Math.min(current * size, total);
            const tpl = t('showing_entries');
            const base = tpl && tpl !== 'showing_entries' && tpl.includes('{from}')
                ? tpl.replace('{from}', String(from)).replace('{to}', String(to)).replace('{total}', String(total))
                : `Showing ${from} to ${to} of ${total} entries`;

            if (!hasAnyFilter.value) return base;
            const filteredTpl = t('filtered');
            const suffix = filteredTpl && filteredTpl !== 'filtered' && filteredTpl.includes('{count}')
                ? filteredTpl.replace('{count}', String(filteredCount.value))
                : `(filtered: ${filteredCount.value})`;
            return `${base} ${suffix}`;
        });

        const debouncedSearch = () => {
            if (searchTimer) clearTimeout(searchTimer);
            searchTimer = setTimeout(() => {
                page.value = 1;
                refreshActiveTab();
            }, 350);
        };

        const goPage = async (p) => {
            const next = Math.min(Math.max(1, Number(p || 1)), totalPages.value);
            if (next === page.value) return;
            page.value = next;
            await refreshActiveTab();
        };

        const nextPage = async () => {
            if (page.value >= totalPages.value) return;
            page.value += 1;
            await refreshActiveTab();
        };

        const prevPage = async () => {
            if (page.value <= 1) return;
            page.value -= 1;
            await refreshActiveTab();
        };

        const goFirst = async () => {
            if (page.value <= 1) return;
            page.value = 1;
            await refreshActiveTab();
        };

        const goLast = async () => {
            if (page.value >= totalPages.value) return;
            page.value = totalPages.value;
            await refreshActiveTab();
        };

        const resetForms = () => {
            planForm.value = {
                plan_name: '',
                plan_name_bn: '',
                vilidity_days: '',
                rate: '',
                rate_foreigner: '',
                max_browsing_qty: '',
                max_contact_view: '',
                can_view_contact_details: false,
                save_favourite_listings: false,
                allow_preference_setup: false,
                get_auto_notification: false,
                view_location_on_map: false,
                order_sl: '',
                status: true,
            };
            pkgForm.value = {
                property_type_id: '',
                package_name: '',
                unit: '',
                range_from: '',
                range_to: '',
                package_rate: '',
                validity_from: '',
                validity_to: '',
                status: true,
            };
            editingId.value = null;
        };

        const openCreate = () => {
            formMode.value = 'create';
            resetForms();
            showFormModal.value = true;
        };

        const openEdit = (row) => {
            formMode.value = 'edit';
            resetForms();

            if (activeTab.value === 'plans') {
                editingId.value = row.PlanID;
                planForm.value.plan_name = row.PlanName || '';
                planForm.value.plan_name_bn = row.PlanName_BN || '';
                planForm.value.vilidity_days = row.VilidityDays ?? '';
                planForm.value.rate = row.Rate ?? '';
                planForm.value.rate_foreigner = row.Rate_Foreigner ?? '';
                planForm.value.max_browsing_qty = row.MaxBrowsingQty ?? '';
                planForm.value.max_contact_view = row.MaxContactView ?? '';
                planForm.value.can_view_contact_details = !!row.CanViewContactDetails;
                planForm.value.save_favourite_listings = !!row.SaveFavouriteListings;
                planForm.value.allow_preference_setup = !!row.AllowPreferenceSetup;
                planForm.value.get_auto_notification = !!row.GetAutoNotification;
                planForm.value.view_location_on_map = !!row.ViewLocationOnMap;
                planForm.value.order_sl = row.Order_SL ?? '';
                planForm.value.status = !!row.Status;
            } else if (activeTab.value === 'owner_rent') {
                editingId.value = row.OwnerPackageRentID;
                pkgForm.value.property_type_id = row.PropertyTypeID ? String(row.PropertyTypeID) : '';
                pkgForm.value.package_name = row.PackageName || '';
                pkgForm.value.unit = row.Unit || '';
                pkgForm.value.range_from = row.RangeFrom ?? '';
                pkgForm.value.range_to = row.RangeTo ?? '';
                pkgForm.value.package_rate = row.PackageRate ?? '';
                pkgForm.value.validity_from = row.ValidityFrom ? String(row.ValidityFrom).slice(0, 10) : '';
                pkgForm.value.validity_to = row.ValidityTo ? String(row.ValidityTo).slice(0, 10) : '';
                pkgForm.value.status = !!row.Status;
            } else {
                editingId.value = row.OwnerPackageSpaceID;
                pkgForm.value.property_type_id = row.PropertyTypeID ? String(row.PropertyTypeID) : '';
                pkgForm.value.package_name = row.PackageName || '';
                pkgForm.value.unit = row.Unit || '';
                pkgForm.value.range_from = row.RangeFrom ?? '';
                pkgForm.value.range_to = row.RangeTo ?? '';
                pkgForm.value.package_rate = row.PackageRate ?? '';
                pkgForm.value.validity_from = row.ValidityFrom ? String(row.ValidityFrom).slice(0, 10) : '';
                pkgForm.value.validity_to = row.ValidityTo ? String(row.ValidityTo).slice(0, 10) : '';
                pkgForm.value.status = !!row.Status;
            }

            showFormModal.value = true;
        };

        const closeForm = () => {
            showFormModal.value = false;
        };

        const save = async () => {
            saving.value = true;
            try {
                if (activeTab.value === 'plans') {
                    const payload = {
                        plan_name: planForm.value.plan_name,
                        plan_name_bn: planForm.value.plan_name_bn || null,
                        vilidity_days: planForm.value.vilidity_days === '' ? null : parseInt(planForm.value.vilidity_days || '0', 10),
                        rate: planForm.value.rate === '' ? null : parseInt(planForm.value.rate || '0', 10),
                        rate_foreigner: planForm.value.rate_foreigner === '' ? null : parseInt(planForm.value.rate_foreigner || '0', 10),
                        max_browsing_qty: planForm.value.max_browsing_qty === '' ? null : parseInt(planForm.value.max_browsing_qty || '0', 10),
                        max_contact_view: planForm.value.max_contact_view === '' ? null : parseInt(planForm.value.max_contact_view || '0', 10),
                        can_view_contact_details: !!planForm.value.can_view_contact_details,
                        save_favourite_listings: !!planForm.value.save_favourite_listings,
                        allow_preference_setup: !!planForm.value.allow_preference_setup,
                        get_auto_notification: !!planForm.value.get_auto_notification,
                        view_location_on_map: !!planForm.value.view_location_on_map,
                        order_sl: planForm.value.order_sl === '' ? null : parseInt(planForm.value.order_sl || '0', 10),
                        status: !!planForm.value.status,
                        lang: langValue(),
                    };
                    if (formMode.value === 'create') {
                        const res = await window.axios.post('/admin/api/package-charges/plans', payload);
                        addToast('success', res.data?.message || t('success'));
                    } else {
                        delete payload.status;
                        const res = await window.axios.put(`/admin/api/package-charges/plans/${editingId.value}`, payload);
                        addToast('success', res.data?.message || t('success'));
                    }
                } else if (activeTab.value === 'owner_rent') {
                    const payload = {
                        property_type_id: parseInt(pkgForm.value.property_type_id || '0', 10),
                        package_name: pkgForm.value.package_name,
                        unit: pkgForm.value.unit,
                        range_from: parseInt(pkgForm.value.range_from || '0', 10),
                        range_to: parseInt(pkgForm.value.range_to || '0', 10),
                        package_rate: parseInt(pkgForm.value.package_rate || '0', 10),
                        validity_from: pkgForm.value.validity_from || null,
                        validity_to: pkgForm.value.validity_to || null,
                        status: !!pkgForm.value.status,
                        lang: langValue(),
                    };
                    if (formMode.value === 'create') {
                        const res = await window.axios.post('/admin/api/package-charges/owner-rent', payload);
                        addToast('success', res.data?.message || t('success'));
                    } else {
                        delete payload.status;
                        const res = await window.axios.put(`/admin/api/package-charges/owner-rent/${editingId.value}`, payload);
                        addToast('success', res.data?.message || t('success'));
                    }
                } else {
                    const payload = {
                        property_type_id: parseInt(pkgForm.value.property_type_id || '0', 10),
                        package_name: pkgForm.value.package_name,
                        unit: pkgForm.value.unit,
                        range_from: pkgForm.value.range_from === '' ? 0 : parseFloat(pkgForm.value.range_from),
                        range_to: pkgForm.value.range_to === '' ? 0 : parseFloat(pkgForm.value.range_to),
                        package_rate: parseInt(pkgForm.value.package_rate || '0', 10),
                        validity_from: pkgForm.value.validity_from || null,
                        validity_to: pkgForm.value.validity_to || null,
                        status: !!pkgForm.value.status,
                        lang: langValue(),
                    };
                    if (formMode.value === 'create') {
                        const res = await window.axios.post('/admin/api/package-charges/owner-space', payload);
                        addToast('success', res.data?.message || t('success'));
                    } else {
                        delete payload.status;
                        const res = await window.axios.put(`/admin/api/package-charges/owner-space/${editingId.value}`, payload);
                        addToast('success', res.data?.message || t('success'));
                    }
                }

                showFormModal.value = false;
                await refreshActiveTab();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                saving.value = false;
            }
        };

        const togglePlan = async (row) => {
            const key = `plan_${row.PlanID}`;
            busyIds.value = { ...busyIds.value, [key]: true };
            try {
                const res = await window.axios.put(`/admin/api/package-charges/plans/${row.PlanID}/status`, { status: !row.Status, lang: langValue() });
                addToast('success', res.data?.message || t('success'));
                await loadPlans();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [key]: false };
            }
        };

        const removePlan = async (planId) => {
            const key = `plan_${planId}`;
            if (!window.confirm(t('confirm_delete'))) return;
            busyIds.value = { ...busyIds.value, [key]: true };
            try {
                const res = await window.axios.delete(`/admin/api/package-charges/plans/${planId}`, { data: { lang: langValue() } });
                addToast('success', res.data?.message || t('success'));
                await loadPlans();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [key]: false };
            }
        };

        const toggleOwnerRent = async (row) => {
            const key = `rent_${row.OwnerPackageRentID}`;
            busyIds.value = { ...busyIds.value, [key]: true };
            try {
                const res = await window.axios.put(`/admin/api/package-charges/owner-rent/${row.OwnerPackageRentID}/status`, { status: !row.Status, lang: langValue() });
                addToast('success', res.data?.message || t('success'));
                await loadOwnerRent();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [key]: false };
            }
        };

        const removeOwnerRent = async (id) => {
            const key = `rent_${id}`;
            if (!window.confirm(t('confirm_delete'))) return;
            busyIds.value = { ...busyIds.value, [key]: true };
            try {
                const res = await window.axios.delete(`/admin/api/package-charges/owner-rent/${id}`, { data: { lang: langValue() } });
                addToast('success', res.data?.message || t('success'));
                await loadOwnerRent();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [key]: false };
            }
        };

        const toggleOwnerSpace = async (row) => {
            const key = `space_${row.OwnerPackageSpaceID}`;
            busyIds.value = { ...busyIds.value, [key]: true };
            try {
                const res = await window.axios.put(`/admin/api/package-charges/owner-space/${row.OwnerPackageSpaceID}/status`, { status: !row.Status, lang: langValue() });
                addToast('success', res.data?.message || t('success'));
                await loadOwnerSpace();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [key]: false };
            }
        };

        const removeOwnerSpace = async (id) => {
            const key = `space_${id}`;
            if (!window.confirm(t('confirm_delete'))) return;
            busyIds.value = { ...busyIds.value, [key]: true };
            try {
                const res = await window.axios.delete(`/admin/api/package-charges/owner-space/${id}`, { data: { lang: langValue() } });
                addToast('success', res.data?.message || t('success'));
                await loadOwnerSpace();
            } catch (e) {
                addToast('error', e?.response?.data?.message || t('failed'));
            } finally {
                busyIds.value = { ...busyIds.value, [key]: false };
            }
        };

        onMounted(async () => {
            await loadPropertyTypes();
            await loadPlans();
        });

        return {
            t,
            activeTab,
            statusFilter,
            propertyTypeFilter,
            searchQuery,
            page,
            perPage,
            pagination,
            propertyTypes,
            plans,
            ownerRent,
            ownerSpace,
            loading,
            saving,
            busyIds,
            showFilters,
            toggleFilters,
            planFilters,
            pkgFilters,
            filteredPlans,
            filteredOwnerRent,
            filteredOwnerSpace,
            hasAnyFilter,
            clearFilters,
            resetPage,
            resetPageAndReload,
            showFormModal,
            formMode,
            editingId,
            planForm,
            pkgForm,
            modalTitle,
            propertyTypeLabel,
            planName,
            planNameAlt,
            setTab,
            refreshActiveTab,
            totalPages,
            pageButtons,
            showingText,
            goPage,
            nextPage,
            prevPage,
            goFirst,
            goLast,
            debouncedSearch,
            openCreate,
            openEdit,
            closeForm,
            save,
            togglePlan,
            removePlan,
            toggleOwnerRent,
            removeOwnerRent,
            toggleOwnerSpace,
            removeOwnerSpace,
            toasts,
            removeToast,
        };
    },
};
</script>

<style scoped>
.page {
    display: flex;
    flex-direction: column;
}

.page-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
}

.page-title {
    font-size: 28px;
    font-weight: 700;
    color: #1a202c;
    margin: 0;
}

.page-subtitle {
    margin: 6px 0 0 0;
    color: #64748b;
    font-size: 14px;
}

.header-actions {
    display: flex;
    gap: 10px;
    align-items: center;
}

.tabs {
    margin-top: 14px;
    display: flex;
    gap: 10px;
    flex-wrap: wrap;
}

.tab {
    padding: 10px 14px;
    border-radius: 12px;
    border: 2px solid #e2e8f0;
    background: #fff;
    font-weight: 800;
    cursor: pointer;
    color: #1e293b;
}

.tab.active {
    border-color: #4A90E2;
    box-shadow: 0 0 0 3px rgba(74, 144, 226, 0.1);
}

.card {
    margin-top: 18px;
    background: white;
    border-radius: 16px;
    padding: 24px;
    box-shadow: 0 2px 8px rgba(0, 0, 0, 0.08);
}

.datatable-toolbar {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    flex-wrap: wrap;
    margin-bottom: 12px;
}

.toolbar-left {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.toolbar-right {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 8px;
}

.length-select,
.status-select {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #ffffff;
    padding: 0 10px;
    font-weight: 800;
    font-size: 12px;
}

.tool-btn {
    height: 32px;
    border-radius: 8px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    padding: 0 12px;
    font-weight: 900;
    font-size: 12px;
    cursor: pointer;
}

.tool-btn-primary {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.tool-btn-icon {
    width: 36px;
    padding: 0;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.tool-btn-light {
    background: #f1f5f9;
    color: #0f172a;
}

.tool-btn-success {
    background: #16a34a;
    color: #ffffff;
    border-color: #16a34a;
}

.tool-btn:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.search-box {
    position: relative;
    max-width: 520px;
    width: 100%;
}

.search-box svg {
    position: absolute;
    left: 14px;
    top: 50%;
    transform: translateY(-50%);
    color: #94a3b8;
}

.search-input {
    width: 100%;
    height: 36px;
    border-radius: 8px;
    border: 2px solid #2563eb;
    padding: 0 12px 0 42px;
    font-size: 13px;
    outline: none;
}

.search-input:focus {
    box-shadow: 0 0 0 3px rgba(37, 99, 235, 0.12);
}

.table-container {
    overflow-x: auto;
}

.data-table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
}

.data-table th {
    text-align: center;
    padding: 10px 8px;
    color: #ffffff;
    font-weight: 900;
    font-size: 12px;
    background: #0b2c3d;
    border-bottom: 1px solid rgba(255, 255, 255, 0.12);
    white-space: nowrap;
}

.th-input,
.th-select {
    width: 100%;
    height: 30px;
    border-radius: 6px;
    border: 1px solid rgba(148, 163, 184, 0.9);
    background: #ffffff;
    padding: 0 8px;
    font-weight: 700;
    font-size: 12px;
    outline: none;
}

.data-table td {
    padding: 9px 8px;
    border-bottom: 1px solid #e2e8f0;
    color: #0f172a;
    font-size: 13px;
    vertical-align: top;
    background: #ffffff;
}

.data-table tbody tr:nth-child(even) td {
    background: rgba(241, 245, 249, 0.55);
}

.skeleton-wrap {
    display: flex;
    flex-direction: column;
    gap: 10px;
}

.skeleton-row {
    height: 12px;
    border-radius: 999px;
    background: linear-gradient(90deg, rgba(226, 232, 240, 0.7), rgba(226, 232, 240, 0.25), rgba(226, 232, 240, 0.7));
    background-size: 200% 100%;
    animation: shimmer 1.2s infinite;
}

@keyframes shimmer {
    0% { background-position: 200% 0; }
    100% { background-position: -200% 0; }
}

.data-table tbody tr:hover td {
    background: rgba(226, 232, 240, 0.55);
}

.cell-main {
    font-weight: 700;
}

.cell-sub {
    color: #64748b;
    font-size: 12px;
    margin-top: 4px;
}

.mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, "Liberation Mono", "Courier New", monospace;
}

.actions {
    display: flex;
    gap: 8px;
    flex-wrap: wrap;
    justify-content: flex-start;
}

.pager {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    padding: 8px 2px 10px;
    flex-wrap: wrap;
}

.pager-left {
    color: #64748b;
    font-weight: 800;
    font-size: 12px;
}

.pager-right {
    display: flex;
    align-items: center;
    gap: 6px;
    flex-wrap: wrap;
}

.pager-btn {
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f1f5f9;
    color: #0f172a;
    padding: 7px 10px;
    border-radius: 6px;
    font-weight: 800;
    cursor: pointer;
    min-width: 66px;
}

.pager-num {
    min-width: 44px;
    height: 32px;
    border: 1px solid rgba(15, 23, 42, 0.12);
    background: #f1f5f9;
    color: #0f172a;
    border-radius: 6px;
    font-weight: 900;
    cursor: pointer;
}

.pager-num.active {
    background: #2563eb;
    color: #ffffff;
    border-color: #2563eb;
}

.pager-btn:disabled,
.pager-num:disabled {
    opacity: 0.6;
    cursor: not-allowed;
}

.btn {
    padding: 10px 16px;
    border-radius: 10px;
    border: 1px solid transparent;
    font-weight: 600;
    cursor: pointer;
    transition: all 0.2s;
    font-size: 14px;
}

.btn-small {
    padding: 8px 10px;
    font-size: 12px;
    border-radius: 10px;
}

.btn-primary {
    background: #4A90E2;
    color: #fff;
}

.btn-secondary {
    background: #ffffff;
    border-color: #e2e8f0;
    color: #1e293b;
}

.btn-danger {
    background: #ef4444;
    color: #fff;
}

.badge {
    display: inline-flex;
    align-items: center;
    padding: 6px 10px;
    border-radius: 999px;
    font-weight: 700;
    font-size: 12px;
}

.badge-success {
    background: rgba(34, 197, 94, 0.15);
    color: #15803d;
}

.badge-muted {
    background: rgba(148, 163, 184, 0.18);
    color: #475569;
}

.loading {
    padding: 18px 0;
    color: #64748b;
}

.empty {
    text-align: center;
    color: #94a3b8;
    padding: 20px;
}

.form-grid {
    display: grid;
    grid-template-columns: repeat(2, minmax(0, 1fr));
    gap: 14px;
}

.form-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
}

.form-label {
    font-size: 13px;
    font-weight: 700;
    color: #1e293b;
}

.form-input {
    padding: 10px 12px;
    border: 2px solid #e2e8f0;
    border-radius: 12px;
    font-size: 14px;
}

.span-2 {
    grid-column: span 2;
}

.modal-overlay {
    position: fixed;
    inset: 0;
    background: rgba(15, 23, 42, 0.65);
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 20px;
    z-index: 1100;
}

.modal {
    width: 100%;
    max-width: 980px;
    background: #fff;
    border-radius: 16px;
    overflow: hidden;
    box-shadow: 0 20px 60px rgba(0, 0, 0, 0.3);
}

.modal-large {
    max-width: 980px;
}

.modal-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 18px 20px;
    border-bottom: 1px solid #eef2f7;
}

.modal-header h3 {
    margin: 0;
    font-size: 18px;
    font-weight: 800;
    color: #0f172a;
}

.modal-close {
    background: transparent;
    border: none;
    font-size: 22px;
    cursor: pointer;
    color: #64748b;
}

.modal-body {
    padding: 20px;
}

.modal-actions {
    padding: 16px 20px;
    border-top: 1px solid #eef2f7;
    display: flex;
    justify-content: flex-end;
    gap: 10px;
}

.toast-container {
    position: fixed;
    top: 20px;
    right: 20px;
    z-index: 2000;
    display: flex;
    flex-direction: column;
    gap: 12px;
}

.toast {
    background: white;
    border-radius: 12px;
    padding: 14px 16px;
    min-width: 320px;
    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.15);
    display: flex;
    align-items: center;
    justify-content: space-between;
    border-left: 4px solid;
}

.toast-success {
    border-left-color: #10b981;
}

.toast-error {
    border-left-color: #ef4444;
}

.toast-message {
    margin: 0;
    color: #1a202c;
    font-weight: 600;
    font-size: 14px;
}

.toast-close {
    background: transparent;
    border: none;
    cursor: pointer;
    color: #64748b;
    padding: 4px;
}

@media (max-width: 720px) {
    .page-header {
        flex-direction: column;
        align-items: flex-start;
        gap: 12px;
    }
    .form-grid {
        grid-template-columns: 1fr;
    }
    .span-2 {
        grid-column: auto;
    }
}
</style>
